import { push } from 'connected-react-router';
import { withFormik } from 'formik';
import { connect } from 'react-redux';
import { compose } from 'recompact';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { errorBoundary, withStandardPageAndLoading } from 'src/app/hocs';
import { accountPage, loggedInTransferRx } from 'src/app/router/paths';
import { loadPatient, segmentTrack, setShowFamilyModal } from 'src/app/state/actions';
import { createDependent } from 'src/app/state/actions/account';
import { getCurrentAccountId } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { PatientDependentDetails } from 'src/app/types';

import { STEP_NAME } from '../../../LoggedInTransfer/Experiment/types';
import { withLoggedInTransferForm } from '../../../LoggedInTransfer/withLoggedInTransferForm';

import validation from './validation';

import Renderer from '.';

function makeMapStateToProps() {
  return (state: RootState) => {
    const accountId = getCurrentAccountId(state);
    const previousPath = state?.routerLocation?.previous?.pathname;
    const client = state.client;
    return {
      accountId,
      previousPath,
      client,
    };
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchCreateDependent: async (accountId: string, values: PatientDependentDetails) =>
    await dispatch(createDependent(accountId, values)),
  dispatchRedirectAccountPage: async () => await dispatch(push(accountPage.toPath())),
  dispatchRedirectTransferPage: async () => await dispatch(push(loggedInTransferRx.toPath())),
  dispatchShowFamilyModal: (dependentName: string) => dispatch(setShowFamilyModal(true, dependentName)),
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
  dispatchLoadPatient: () => dispatch(loadPatient()),
});

const enhanceWithFormik = withFormik({
  displayName: 'DeliveryAddressCreateForm',
  mapPropsToValues: () => validation.defaultValues,
  validationSchema: validation.schema,
  validateOnChange: true,
  validateOnBlur: true,
  handleSubmit: async (values, { props, setSubmitting }) => {
    const {
      accountId,
      dispatchCreateDependent,
      dispatchRedirectAccountPage,
      dispatchSegmentTrack,
      previousPath,
      dispatchRedirectTransferPage,
      dispatchShowFamilyModal,
      dispatchLoadPatient,
      setLoggedInTransferFormData,
      setLoggedInTransferFormProgress,
      loggedInTransferFormData,
      loggedInTransferFormProgress,
    } = props;
    setSubmitting(true);

    try {
      // Storing the response because we need patient id for highlighting
      // the transfer form when they add a family member
      const response = await dispatchCreateDependent(accountId, values);
      const patientDetails = response?.data;
      const patientId = patientDetails.id;
      await dispatchLoadPatient();

      if (previousPath === accountPage.route) {
        dispatchShowFamilyModal(`${values.firstName} ${values.lastName}`);
        dispatchRedirectAccountPage();
        dispatchSegmentTrack(UNIFIED.addFamilyMember, { source: 'Account' });
      } else if (previousPath === loggedInTransferRx.route) {
        setLoggedInTransferFormData({
          ...loggedInTransferFormData,
          patientId,
          patientName: `${values.firstName} ${values.lastName}`,
          birthdate: values.birthdate,
          patientIsPrimary: false,
        });
        await setLoggedInTransferFormProgress({
          steps: loggedInTransferFormProgress.steps.includes(STEP_NAME.PATIENT)
            ? loggedInTransferFormProgress.steps
            : [STEP_NAME.PATIENT, ...loggedInTransferFormProgress.steps],
          currentStepIndex: 0,
        });
        dispatchRedirectTransferPage();
        dispatchSegmentTrack(UNIFIED.addFamilyMember, { source: 'Transfer Form' });
      } else {
        dispatchShowFamilyModal(`${values.firstName} ${values.lastName}`);
        dispatchRedirectAccountPage();
        dispatchSegmentTrack(UNIFIED.addFamilyMember);
      }
    } catch (error: any) {
      const error_code = error?.response?.status;
      const error_summary = error?.response?.statusText;
      if (previousPath === accountPage.route) {
        dispatchSegmentTrack(UNIFIED.addFamilyMemberFailed, {
          source: 'Account',
          error_code,
          error_summary,
        });
      } else if (previousPath === loggedInTransferRx.route) {
        dispatchSegmentTrack(UNIFIED.addFamilyMemberFailed, {
          source: 'Transfer Form',
          error_code,
          error_summary,
        });
      } else {
        dispatchSegmentTrack(UNIFIED.addFamilyMemberFailed, {
          error_code,
          error_summary,
        });
      }
      setSubmitting(false);
    }
  },
});

export default compose(
  errorBoundary,
  connect(makeMapStateToProps, mapDispatchToProps),
  withLoggedInTransferForm,
  withStandardPageAndLoading('Add a family member'),
  enhanceWithFormik
)(Renderer);
